import type { Manifest } from '@clawharbor/manifest';
import { CINEPLEXX_MANIFEST } from './fixtures/cineplexx.js';
import { HAK_MANIFEST } from './fixtures/hak.js';
import type { ProgressEvent } from './types.js';

/**
 * Four recorded crawls.
 *
 * The console used to replay one script for every example on the landing page,
 * which meant the row promising "3 of 4 turned back at the gate" produced the
 * cinema crawl. A product whose entire claim is "we show you what we could not
 * verify" cannot ship a demo that lies about which site it is showing, so every
 * example now replays its OWN recording, and the three outcomes a real operator
 * meets are all reachable:
 *
 *   published        capabilities cleared and there is something to publish
 *   nothing-cleared   the site was read, the brand came back, no verb survived
 *   refused           the crawl never started, and refusing was the correct act
 *
 * Every line below is verbatim from evals/results/sites/*.json — the 51-site run
 * documented in docs/COVERAGE.md, crawled at 1 rps with --no-llm. Only two
 * things are not faithful and both are stated in the UI: wall clock is
 * compressed roughly 2-4x, and the published URLs are placeholders.
 */

export interface Frame {
  at: number;
  event: ProgressEvent;
}

export type Outcome = 'published' | 'nothing-cleared' | 'refused';

export interface Scenario {
  /** Matched against the hostname the operator typed. */
  hosts: readonly string[];
  url: string;
  label: string;
  /** The landing-page one-liner. Must describe THIS recording, not the product. */
  note: string;
  outcome: Outcome;
  script: Frame[];
  /** Null when the crawl never produced one. */
  manifest: Manifest | null;
  urls?: { webUrl: string; mcpUrl: string };
}

/**
 * hak.hr — the gate doing its job in public.
 *
 * Ten heuristic proposals, four cleared, six turned back, and the six reasons
 * are the two commonest failures in the whole corpus: a selector that 404s, and
 * a query parameter with no vocabulary to search its value space. One of the
 * six (`query_goashx`) returned a row and was STILL refused, because a single
 * row from an unsearchable parameter is not a capability an agent can use.
 */
export const HAK: Scenario = {
  hosts: ['hak.hr', 'www.hak.hr'],
  url: 'https://www.hak.hr',
  label: 'hak.hr',
  note: 'motoring club · 10 proposed, 6 turned back at the gate',
  outcome: 'published',
  manifest: HAK_MANIFEST,
  urls: {
    webUrl: 'https://clawharbor.app/t/hak-hr',
    mcpUrl: 'https://clawharbor.app/t/hak-hr/mcp',
  },
  script: [
  { at: 120, event: { phase: "intake", pct: 2, message: "── 1/6 intake" } },
  { at: 335, event: { phase: "intake", pct: 5, message: "   origin  https://www.hak.hr" } },
  { at: 558, event: { phase: "intake", pct: 9, message: "   robots  disallowAll=false rules=0" } },
  { at: 781, event: { phase: "intake", pct: 12, message: "   stack   {\"rendering\":\"server\"}" } },
  { at: 1004, event: { phase: "observe", pct: 13, message: "── 2/6 observe" } },
  { at: 1252, event: { phase: "observe", pct: 15, message: "   sitemap: 0 url(s)" } },
  { at: 1476, event: { phase: "observe", pct: 16, message: "   induced 0 url template(s)" } },
  { at: 1699, event: { phase: "observe", pct: 18, message: "   fetching up to 8 page(s) at 1 rps" } },
  { at: 1922, event: { phase: "observe", pct: 20, message: "     200 https://www.hak.hr/" } },
  { at: 2096, event: { phase: "observe", pct: 21, message: "     404 https://www.hak.hr/info/korisne-informacije/zabrane-za-teretna-vozila/)" } },
  { at: 2269, event: { phase: "observe", pct: 23, message: "     200 https://www.hak.hr/sigurnost-u-prometu/projekti/ersc" } },
  { at: 2443, event: { phase: "observe", pct: 25, message: "     200 https://www.hak.hr/sigurnost-u-prometu/projekti/irap/" } },
  { at: 2616, event: { phase: "observe", pct: 26, message: "     200 https://www.hak.hr/vozila/tehnicki-pregledi/popis-stp/" } },
  { at: 2790, event: { phase: "observe", pct: 28, message: "     200 https://www.hak.hr/vozila/tehnicki-pregledi/kalkulator/" } },
  { at: 2964, event: { phase: "observe", pct: 30, message: "     200 https://www.hak.hr/vijest/2425/hak-proslavio-120-godina" } },
  { at: 3137, event: { phase: "observe", pct: 31, message: "     200 https://www.hak.hr/sigurnost-u-prometu/projekti/eurotap/" } },
  { at: 3311, event: { phase: "observe", pct: 33, message: "   found 5 candidate endpoint(s)" } },
  { at: 3534, event: { phase: "brand", pct: 36, message: "── 3/6 brand" } },
  { at: 3782, event: { phase: "brand", pct: 39, message: "   primary #4b87b8 (confidence 0.7)" } },
  { at: 4005, event: { phase: "brand", pct: 43, message: "   mode    dark" } },
  { at: 4228, event: { phase: "brand", pct: 46, message: "   type    HelveticaNeue" } },
  { at: 4452, event: { phase: "synthesize", pct: 49, message: "── 4/6 synthesize" } },
  { at: 4700, event: { phase: "synthesize", pct: 50, message: "   heuristic proposed 10 capabilit(ies)" } },
  { at: 4923, event: { phase: "synthesize", pct: 51, message: "   · list_items (heuristic)" } },
  { at: 5146, event: { phase: "synthesize", pct: 51, message: "   · list_infos (heuristic)" } },
  { at: 5369, event: { phase: "synthesize", pct: 52, message: "   · list_sigurnost_u_prometus (heuristic)" } },
  { at: 5592, event: { phase: "synthesize", pct: 53, message: "   · list_vozilas (heuristic)" } },
  { at: 5816, event: { phase: "synthesize", pct: 54, message: "   · list_vijests (heuristic)" } },
  { at: 6039, event: { phase: "synthesize", pct: 55, message: "   · query_tollashx (heuristic)" } },
  { at: 6262, event: { phase: "synthesize", pct: 56, message: "   · query_searchashx (heuristic)" } },
  { at: 6485, event: { phase: "synthesize", pct: 56, message: "   · query_goashx (heuristic)" } },
  { at: 6708, event: { phase: "synthesize", pct: 57, message: "   · query_newsletter (heuristic)" } },
  { at: 6932, event: { phase: "synthesize", pct: 58, message: "   · query_locationsashx (heuristic)" } },
  { at: 7155, event: { phase: "probe", pct: 60, message: "── 5/6 probe (the gate — unverified capabilities are dropped)" } },
  { at: 7403, event: { phase: "probe", pct: 61, message: "     list_items: 23 row(s)" } },
  { at: 7576, event: { phase: "probe", pct: 63, message: "     list_infos: FAILED — step 1 http.html → rows: origin returned 404" } },
  { at: 7750, event: { phase: "probe", pct: 64, message: "     list_sigurnost_u_prometus: 23 row(s)" } },
  { at: 7924, event: { phase: "probe", pct: 66, message: "     list_vozilas: 23 row(s)" } },
  { at: 8097, event: { phase: "probe", pct: 67, message: "     list_vijests: 23 row(s)" } },
  { at: 8271, event: { phase: "probe", pct: 68, message: "     query_tollashx: FAILED — step 1 http.json → rows: expected JSON, got E…" } },
  { at: 8444, event: { phase: "probe", pct: 70, message: "     query_searchashx: FAILED — step 1 http.json → rows: expected JSON, got <li>Nema rezultata za Vaš upit</li>…" } },
  { at: 8618, event: { phase: "probe", pct: 71, message: "     query_goashx: 1 row(s)" } },
  { at: 8792, event: { phase: "probe", pct: 73, message: "     query_newsletter: FAILED — step 1 http.json → rows: origin returned 404" } },
  { at: 8965, event: { phase: "probe", pct: 74, message: "     query_locationsashx: FAILED — step 1 http.json → rows: expected JSON, got <li id=\"l397\">Zagreb</li>\n<li id=\"l335\">Split</li>\n<li id=\"l309\">Rijeka</li>\n<li…" } },
  { at: 9139, event: { phase: "probe", pct: 75, message: "   PASS  list_items  rows=23 pass=100%" } },
  { at: 9362, event: { phase: "probe", pct: 77, message: "   DROP  list_infos  rows=0 pass=0%  — step 1 http.html → rows: origin returned 404" } },
  { at: 9585, event: { phase: "probe", pct: 78, message: "   PASS  list_sigurnost_u_prometus  rows=23 pass=100%" } },
  { at: 9808, event: { phase: "probe", pct: 80, message: "   PASS  list_vozilas  rows=23 pass=100%" } },
  { at: 10032, event: { phase: "probe", pct: 81, message: "   PASS  list_vijests  rows=23 pass=100%" } },
  { at: 10255, event: { phase: "probe", pct: 82, message: "   DROP  query_tollashx  rows=0 pass=0%  — no candidate vocabulary for parameter \"o\"; cannot search its value space" } },
  { at: 10478, event: { phase: "probe", pct: 84, message: "   DROP  query_searchashx  rows=0 pass=0%  — no candidate vocabulary for parameter \"q\"; cannot search its value space" } },
  { at: 10701, event: { phase: "probe", pct: 85, message: "   DROP  query_goashx  rows=1 pass=100%  — no candidate vocabulary for parameter \"url\"; cannot search its value space" } },
  { at: 10924, event: { phase: "probe", pct: 87, message: "   DROP  query_newsletter  rows=0 pass=0%  — no candidate vocabulary for parameter \"email\"; cannot search its value space" } },
  { at: 11148, event: { phase: "probe", pct: 88, message: "   DROP  query_locationsashx  rows=0 pass=0%  — no candidate vocabulary for parameter \"q\"; cannot search its value space" } },
  { at: 11371, event: { phase: "assemble", pct: 100, message: "── 6/6 assemble" } },
    { at: 11619, event: { phase: 'assemble', pct: 100, status: 'ready' } },
  ],
};

/**
 * cineplexx.hr — the common outcome.
 *
 * A 5.5 kB JavaScript shell. Brand extraction drove a real browser against the
 * same URL and returned #de1619 at 0.95 confidence, the highest in the corpus.
 * Capability observation, reading the served HTML, proposed nothing at all.
 * docs/COVERAGE.md: 46 of 51 sites produced a brand; 5 produced a usable verb.
 * This is what the other 46 look like, and it needs a designed screen.
 */
export const CINEPLEXX: Scenario = {
  hosts: ['cineplexx.hr', 'www.cineplexx.hr'],
  url: 'https://www.cineplexx.hr',
  label: 'cineplexx.hr',
  note: 'cinema chain · brand at 0.95, zero verbs — the common outcome',
  outcome: 'nothing-cleared',
  manifest: CINEPLEXX_MANIFEST,
  script: [
  { at: 120, event: { phase: "intake", pct: 2, message: "── 1/6 intake" } },
  { at: 335, event: { phase: "intake", pct: 5, message: "   origin  https://www.cineplexx.hr" } },
  { at: 558, event: { phase: "intake", pct: 9, message: "   robots  disallowAll=false rules=0" } },
  { at: 781, event: { phase: "intake", pct: 12, message: "   stack   {\"server\":\"nginx\",\"rendering\":\"client\"}" } },
  { at: 1004, event: { phase: "observe", pct: 13, message: "── 2/6 observe" } },
  { at: 1252, event: { phase: "observe", pct: 17, message: "   sitemap: 0 url(s)" } },
  { at: 1476, event: { phase: "observe", pct: 21, message: "   induced 0 url template(s)" } },
  { at: 1699, event: { phase: "observe", pct: 25, message: "   fetching up to 8 page(s) at 1 rps" } },
  { at: 1922, event: { phase: "observe", pct: 29, message: "     200 https://www.cineplexx.hr/" } },
  { at: 2096, event: { phase: "observe", pct: 33, message: "   found 0 candidate endpoint(s)" } },
  { at: 2319, event: { phase: "brand", pct: 36, message: "── 3/6 brand" } },
  { at: 2567, event: { phase: "brand", pct: 39, message: "   primary #de1619 (confidence 0.95)" } },
  { at: 2790, event: { phase: "brand", pct: 43, message: "   mode    dark" } },
  { at: 3013, event: { phase: "brand", pct: 46, message: "   type    sans-serif" } },
  { at: 3236, event: { phase: "synthesize", pct: 49, message: "── 4/6 synthesize" } },
  { at: 3484, event: { phase: "synthesize", pct: 58, message: "   heuristic proposed 0 capabilit(ies)" } },
  { at: 3708, event: { phase: "probe", pct: 88, message: "── 5/6 probe (the gate — unverified capabilities are dropped)" } },
  { at: 3956, event: { phase: "assemble", pct: 100, message: "── 6/6 assemble" } },
    { at: 4204, event: { phase: 'assemble', pct: 100, status: 'ready' } },
  ],
};

/**
 * zaba.hr — refusing to crawl.
 *
 * robots.txt disallows everything for our user agent, so the run stops at
 * intake. This is not a bug and the screen must not read like one: it is the
 * politeness rule working, and the only honest next step is to ask the site's
 * owner, which is a different product motion entirely.
 */
export const ZABA: Scenario = {
  hosts: ['zaba.hr', 'www.zaba.hr'],
  url: 'https://www.zaba.hr',
  label: 'zaba.hr',
  note: 'bank · robots.txt disallows our agent — we stop',
  outcome: 'refused',
  manifest: null,
  script: [
    { at: 120, event: { phase: 'intake', pct: 2, message: '── 1/6 intake' } },
    { at: 384, event: { phase: 'intake', pct: 5, message: '   origin  https://www.zaba.hr' } },
    { at: 670, event: { phase: 'intake', pct: 8, message: '   robots  disallowAll=true rules=7' } },
    { at: 942, event: { phase: 'intake', pct: 10, message: '   stack   {"rendering":"client"}' } },
    {
      at: 2100,
      event: {
        status: 'failed',
        error:
          'https://www.zaba.hr/robots.txt disallows everything for our user agent. Refusing to crawl.',
      },
    },
  ],
};
